import {
  type Address,
  addresses,
  type ChainAddresses,
  ChainId,
  Eip5267Domain,
  erc20WrapperTokens,
  Token
} from '@morpho-org/blue-sdk'

declare module '@morpho-org/blue-sdk' {
  interface ChainAddresses {
    timeBoundedUrd?: Address
    marketRewardsProgramRegistry?: Address

    weEth?: Address
    ezEth?: Address
    rsEth?: Address
    usdE?: Address
    sUsdE?: Address
    'usd0++'?: Address
    'usd0usd0++'?: Address
    verUsdc?: Address
    rsweth?: Address
    bsdEth?: Address
    re7Weth?: Address

    legacyMorphoToken?: Address
    deprecatedMorphoToken?: Address
    morphoToken?: Address
    morphoTokenWrapper?: Address
  }
}

interface MainnetAddresses extends ChainAddresses {
  legacyMorphoToken: Address
  deprecatedMorphoToken: Address
  morphoToken: Address
  morphoTokenWrapper: Address
}

interface BaseAddresses extends ChainAddresses {
  deprecatedMorphoToken: Address
  morphoToken: Address
  wellToken: Address
}

// oxlint-disable-next-line typescript/consistent-type-assertions
export const mainnetAddresses = {
  ...addresses[ChainId.EthMainnet]
} as unknown as MainnetAddresses

mainnetAddresses.timeBoundedUrd = '0x330eefa8a787552DC5cAd3C3cA644844B1E61Ddb'
mainnetAddresses.marketRewardsProgramRegistry = '0x5148bF15bb722E1854F66430Bc9FeD0E9FDaCE7D'
mainnetAddresses.weEth = '0xCd5fE23C85820F7B72D0926FC9b05b43E359b7ee'
mainnetAddresses.ezEth = '0xbf5495Efe5DB9ce00f80364C8B423567e58d2110'
mainnetAddresses.rsEth = '0xA1290d69c65A6Fe4DF752f95823fae25cB99e5A7'
mainnetAddresses.usdE = '0x4c9EDD5852cd905f086C759E8383e09bff1E68B3'
mainnetAddresses.sUsdE = '0x9D39A5DE30e57443BfF2A8307A4256c8797A3497'
mainnetAddresses['usd0++'] = '0x35D8949372D46B7a3D5A56006AE77B215fc69bC0'
mainnetAddresses['usd0usd0++'] = '0x1d08E7adC263CfC70b1BaBe6dC5Bb339c16Eec52'
mainnetAddresses.rsweth = '0xFAe103DC9cf190eD75350761e95403b7b8aFa6c0'
mainnetAddresses.re7Weth = '0x78Fc2c2eD1A4cDb5402365934aE5648aDAd094d0'

mainnetAddresses.legacyMorphoToken = '0x9994E35Db50125E0DF82e4c2dde62496CE330999'
mainnetAddresses.deprecatedMorphoToken = '0x039b598c6b99e70058E1e9021E000BdacD33d026'
mainnetAddresses.morphoToken = '0x58D97B57BB95320F9a05dC918Aef65434969c2B2'
mainnetAddresses.morphoTokenWrapper = '0x9D03bb2092270648d7480049d0E58d2FcF0E5123'

/** Vault V1 PublicAllocator deployments; removed from blue-sdk v7, kept here for the V1 curation UI. */
export const vaultV1PublicAllocatorAddresses: Partial<Record<ChainId, Address>> = {
  [ChainId.EthMainnet]: '0xfd32fA2ca22c76dD6E550706Ad913FC6CE91c75D',
  [ChainId.BaseMainnet]: '0xA090dD1a701408Df1d4d0B85b716c87565f90467',
  [ChainId.PolygonMainnet]: '0xfac15aff53ADd2ff80C2962127C434E8615Df0d3',
  [ChainId.ArbitrumMainnet]: '0x769583Af5e9D03589F159EbEC31Cc2c23E8C355E',
  [ChainId.OptimismMainnet]: '0x0d68a97324E602E02799CD83B42D337207B40658',
  [ChainId.WorldChainMainnet]: '0xef9889B4e443DEd35FA0Bd060f2104Cca94e6A43',
  [ChainId.FraxtalMainnet]: '0x37a888192165fC39884f87c64E2476BfD2C09675',
  [ChainId.ScrollMainnet]: '0x8a7f671E45E51dE245649Cf916cA0256FB8a9927',
  [ChainId.InkMainnet]: '0x85416891752a6B81106c1C2999AE1AF5d8Cd3357',
  [ChainId.Unichain]: '0xB0c9a107fA17c779B3378210A7a593e88938C7C9',
  [ChainId.SonicMainnet]: '0x6Cef2EDC70D87E8f1623f3096efF05d066E59B36',
  [ChainId.HemiMainnet]: '0x4107Ea1746909028d6212B315dE5fE9538F9eb39',
  [ChainId.ModeMainnet]: '0xEE868Bf3359DA30c10ea472EAEBFC0a06E8F0120',
  [ChainId.CornMainnet]: '0xDFde06e2B2A2D718eE5560b73dA4F830E56A2f10',
  [ChainId.PlumeMainnet]: '0x58485338D93F4e3b4Bf2Af1C9f9C0aDF087AEf1C',
  [ChainId.CampMainnet]: '0x0b7a3A49dafd98363B428cEC966106f29c0eee75',
  [ChainId.KatanaMainnet]: '0x39EB6Da5e88194C82B13491Df2e8B3E213eD2412',
  [ChainId.EtherlinkMainnet]: '0x8b8B1bd41d36c06253203CD21463994aB752c1e6',
  [ChainId.TacMainnet]: '0x414247afcf1fE3b94C617e7E3A7adB81D8D3208F',
  [ChainId.LiskMainnet]: '0xb1E5B1De2a54ab55C412B5ee1E38e46799588103',
  [ChainId.HyperliquidMainnet]: '0x517505be22D9068687334e69ae7a02fC77edf4Fc',
  [ChainId.SeiMainnet]: '0xD878509446bE2C601f0f032F501851001B159D6B',
  [ChainId.ZeroGMainnet]: '0x28a80F3713735CAD44bD9d7E1da9Fa16b0244497',
  [ChainId.LineaMainnet]: '0x2d4cf00e18D48fD030d9b1E2FAAE6e0384C7610B',
  [ChainId.MonadMainnet]: '0xfd70575B732F9482F4197FE1075492e114E97302',
  [ChainId.StableMainnet]: '0xbCB063D4B6D479b209C186e462828CBACaC82DbE',
  [ChainId.CronosMainnet]: '0xFEAbEf95f3C937Ff4d5fD70005CF3392f8Ca02d5',
  [ChainId.CeloMainnet]: '0x3Fe12193D178B76BaF4e23a083A64e49ACDE3188',
  [ChainId.AbstractMainnet]: '0x609f3DF49806B5617A0Bd3301f04Ac3CB26d9e82',
  [ChainId.BitlayerMainnet]: '0x3A1db0038361528756bED147abe3d41255c7128c',
  [ChainId.BscMainnet]: '0x842bEccF8eBC11006c4bE96DEfE09b60326D0495',
  [ChainId.SoneiumMainnet]: '0x76f93A21573014Ab7d634D3204818922A234249e'
}

/** @internal */
export const LEGACY_MORPHO_TOKEN = new Token({
  address: mainnetAddresses.legacyMorphoToken,
  decimals: 18,
  symbol: 'MORPHO',
  name: 'Morpho Token',
  eip5267Domain: new Eip5267Domain({
    fields: '0xf',
    name: 'Morpho Token',
    version: '1',
    chainId: BigInt(ChainId.EthMainnet),
    verifyingContract: mainnetAddresses.legacyMorphoToken,
    salt: '0x0',
    extensions: []
  })
})

erc20WrapperTokens[ChainId.EthMainnet]?.add(mainnetAddresses.morphoTokenWrapper)

// oxlint-disable-next-line typescript/consistent-type-assertions
export const baseAddresses = {
  ...addresses[ChainId.BaseMainnet]
} as unknown as BaseAddresses

baseAddresses.ezEth = '0x2416092f143378750bb29b79eD961ab195CcEea5'
baseAddresses.bsdEth = '0xCb327b99fF831bF8223cCEd12B1338FF3aA322Ff'
baseAddresses.deprecatedMorphoToken = '0x039b598c6b99e70058E1e9021E000BdacD33d026'
baseAddresses.morphoToken = '0xBAa5CC21fd487B8Fcc2F632f3F4E8D37262a0842'
baseAddresses.wellToken = '0xA88594D404727625A9437C3f886C7643872296AE'
